export * from './shopping-lists';

import { z } from 'zod';

// =============================================================================
// Password Validation
// =============================================================================

/**
 * Password requirements configuration.
 * Used for both validation and UI display.
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
} as const;

/**
 * Password validation schema.
 * Enforces: 8+ chars, uppercase, lowercase, number.
 */
export const passwordSchema = z
  .string()
  .min(PASSWORD_REQUIREMENTS.minLength, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

/**
 * Password check results for UI display.
 */
export interface PasswordChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
}

/**
 * Validate a password and return individual check results.
 * Use this for real-time UI feedback.
 */
export function getPasswordChecks(password: string): PasswordChecks {
  return {
    length: password.length >= PASSWORD_REQUIREMENTS.minLength,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
}

/**
 * Check if all password requirements are met.
 */
export function isPasswordValid(password: string): boolean {
  const checks = getPasswordChecks(password);
  return checks.length && checks.uppercase && checks.lowercase && checks.number;
}

/**
 * Calculate password strength (1-3) for strength indicator.
 * 1 = Weak, 2 = Medium, 3 = Strong
 */
export function getPasswordStrength(password: string): { strength: 0 | 1 | 2 | 3; label: string } {
  if (!password) return { strength: 0, label: '' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++; // Special chars bonus

  if (score <= 2) return { strength: 1, label: 'Weak' };
  if (score <= 4) return { strength: 2, label: 'Medium' };
  return { strength: 3, label: 'Strong' };
}

// =============================================================================
// Household schemas
// =============================================================================

// Household schemas
export const householdSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type HouseholdSchema = z.infer<typeof householdSchema>;

// Household role enum
export const householdRoleSchema = z.enum(['owner', 'admin', 'member']);

export type HouseholdRoleSchema = z.infer<typeof householdRoleSchema>;

// Household member schemas
export const householdMemberSchema = z.object({
  id: z.string().uuid(),
  householdId: z.string().uuid(),
  userId: z.string().uuid(),
  role: householdRoleSchema,
  joinedAt: z.coerce.date(),
});

export type HouseholdMemberSchema = z.infer<typeof householdMemberSchema>;
